import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import useTable from '@/hooks/useTable';
import type { GsBulletin } from '@/lib/types';
import DemoBadge from '@/components/feature/DemoBadge';
import { REGIONS } from '@/pages/demo/revenue/components/revenueData';
import { regionName } from './adminData';

type Status = 'pending' | 'approved' | 'rejected';

type Row = {
  key: string;
  tag: string;
  title: string;
  body: string;
  meta: string;
  contact: string;
  submitter: string;
  status: Status;
  local: boolean;
  regions: string[];
};

type Filter = 'pending' | 'approved' | 'rejected' | 'all';

const TAGS = ['NOTICE', 'EVENT', 'VOLUNTEER', 'CLUB', 'COURSE', 'LOST & FOUND'];

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'pending', label: 'Needs review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Not approved' },
  { id: 'all', label: 'All' },
];

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-3.5 py-2.5 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1.5';

function statusStyle(status: Status) {
  if (status === 'approved') return 'bg-primary-500 text-background-50';
  if (status === 'rejected') return 'bg-background-200 text-foreground-600 border border-background-300';
  return 'bg-accent-100 text-accent-900 border border-accent-300';
}

function statusLabel(status: Status) {
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Not approved';
  return 'Pending';
}

export default function BulletinManager() {
  const { data: bulletins, loading, error, reload } = useTable<GsBulletin>('gs_bulletins');
  const [decisions, setDecisions] = useState<Record<string, Status>>({});
  const [added, setAdded] = useState<Row[]>([]);
  const [flash, setFlash] = useState('');
  const [filter, setFilter] = useState<Filter>('pending');
  const [selected, setSelected] = useState<string | null>(null);
  const [regionIds, setRegionIds] = useState<string[]>([]);

  const seeded: Row[] = useMemo(
    () => bulletins.map((b) => ({
      key: `db-${b.id}`,
      tag: b.tag,
      title: b.title,
      body: b.body || '',
      meta: b.meta || '',
      contact: b.contact || '',
      submitter: b.submitter_name || 'A GreenSide neighbor',
      status: (b.status as Status) || 'pending',
      local: false,
      regions: [],
    })),
    [bulletins],
  );

  const allRows = useMemo(
    () => [...added, ...seeded].map((r) => ({ ...r, status: decisions[r.key] ?? r.status })),
    [added, seeded, decisions],
  );

  const rows = useMemo(
    () => (filter === 'all' ? allRows : allRows.filter((r) => r.status === filter)),
    [allRows, filter],
  );

  const counts = useMemo(() => ({
    pending: allRows.filter((r) => r.status === 'pending').length,
    approved: allRows.filter((r) => r.status === 'approved').length,
    rejected: allRows.filter((r) => r.status === 'rejected').length,
    all: allRows.length,
  }), [allRows]);

  const activeRow = useMemo(() => allRows.find((r) => r.key === selected) || null, [allRows, selected]);

  const decide = (row: Row, next: Status) => {
    setDecisions((prev) => ({ ...prev, [row.key]: next }));
    if (next === 'approved') {
      setFlash(row.local
        ? 'Approved locally — in the live product this notice would now appear on the Community Board.'
        : 'Approval is simulated in this preview. The live board is unchanged — in production this step would publish the notice.');
    } else if (next === 'rejected') {
      setFlash('Marked as not approved in this preview only. Nothing on the live site changes.');
    } else {
      setFlash('Returned to the review queue in this preview only. Nothing on the live site changes.');
    }
    setTimeout(() => setFlash(''), 4500);
  };

  const compose = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const row: Row = {
      key: `local-${Date.now()}`,
      tag: String(data.get('tag') || 'NOTICE'),
      title: String(data.get('title') || 'Untitled notice'),
      body: String(data.get('body') || ''),
      meta: String(data.get('meta') || ''),
      contact: String(data.get('contact') || ''),
      submitter: String(data.get('submitter') || 'A GreenSide neighbor'),
      status: 'pending',
      local: true,
      regions: regionIds,
    };
    setAdded((prev) => [row, ...prev]);
    setSelected(row.key);
    setFilter('pending');
    setFlash('Draft notice added to the review queue (local only — not saved to the Backend).');
    setTimeout(() => setFlash(''), 4500);
    form.reset();
    setRegionIds([]);
  };

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-heading font-extrabold uppercase text-3xl text-foreground-950">Community bulletins</h2>
        <DemoBadge label="Demo — nothing is saved" />
      </div>

      <div className="bg-secondary-50 border border-secondary-200 rounded-lg px-4 py-3 flex items-start gap-3 mb-6">
        <span className="w-5 h-5 flex items-center justify-center text-secondary-700 shrink-0 mt-0.5"><i className="ri-information-line"></i></span>
        <p className="text-sm text-secondary-900">
          <strong className="font-semibold">How moderation works.</strong> Visitor notices arrive here with a <em>Pending</em> status. Approving one publishes it to the public Community Board; until then it stays hidden. Real submissions are read from the live site — your approve / reject decisions in this preview are visual only and never change the live board.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-5 space-y-6">
          <form onSubmit={compose} className="bg-background-50 border border-background-200 rounded-lg p-5">
            <h3 className="font-heading font-bold text-xl text-foreground-950">Compose a notice</h3>
            <p className="mt-1 text-xs text-foreground-500">Drafts start as Pending. Use this to show how an editor would add a notice on a visitor&apos;s behalf.</p>
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="b-submitter" className={labelClass}>Posted by</label>
                  <input id="b-submitter" name="submitter" placeholder="e.g. Dana R." className={fieldClass} />
                </div>
                <div>
                  <label htmlFor="b-tag" className={labelClass}>Notice type</label>
                  <select id="b-tag" name="tag" className={`${fieldClass} cursor-pointer`} defaultValue={TAGS[0]}>
                    {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="b-title" className={labelClass}>Headline</label>
                <input id="b-title" name="title" required maxLength={120} placeholder="e.g. Looking for a fourth for Saturday" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="b-body" className={labelClass}>Message</label>
                <textarea id="b-body" name="body" required maxLength={500} rows={3} placeholder="Tell neighbors what's happening, when and where." className={`${fieldClass} resize-none`}></textarea>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="b-meta" className={labelClass}>Location</label>
                  <input id="b-meta" name="meta" placeholder="e.g. Long Beach" className={fieldClass} />
                </div>
                <div>
                  <label htmlFor="b-contact" className={labelClass}>Contact (optional)</label>
                  <input id="b-contact" name="contact" placeholder="Email / phone" className={fieldClass} />
                </div>
              </div>
              <div>
                <span className={labelClass}>Target regions (optional)</span>
                <div className="flex flex-wrap gap-2">
                  {REGIONS.map((r) => {
                    const on = regionIds.includes(r.id);
                    return (
                      <button key={r.id} type="button" onClick={() => setRegionIds((prev) => (on ? prev.filter((x) => x !== r.id) : [...prev, r.id]))} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}>
                        <span className="w-3.5 h-3.5 flex items-center justify-center">{on ? <i className="ri-checkbox-circle-line"></i> : <i className="ri-add-circle-line"></i>}</span>
                        {r.city}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <button type="submit" className="mt-4 w-full bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
              Add to review queue
            </button>
          </form>

          <div className="bg-background-50 border border-background-200 rounded-lg p-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-heading font-bold text-lg text-foreground-950">Board preview</h3>
              <DemoBadge label="Sample card" />
            </div>
            {activeRow ? (
              <article className="relative mt-5 bg-background-100 border border-background-200 rounded-md p-5 pt-7">
                <span aria-hidden="true" className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-5 h-5">
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3.5 h-2 rounded-full bg-foreground-950/15 blur-[2px]"></span>
                  <span className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-300 via-primary-500 to-primary-800"></span>
                  <span className="absolute top-[3px] left-[5px] w-1.5 h-1.5 rounded-full bg-background-50/80"></span>
                </span>
                <span className="inline-block bg-primary-50 text-primary-700 font-label font-semibold uppercase text-[11px] tracking-[0.16em] px-2.5 py-1 rounded-sm">{activeRow.tag}</span>
                <h4 className="mt-3 font-heading font-bold text-xl leading-snug text-foreground-950">{activeRow.title}</h4>
                {activeRow.body && <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{activeRow.body}</p>}
                {activeRow.meta && (
                  <p className="mt-4 pt-3 border-t border-background-200 flex items-center gap-1.5 font-label uppercase tracking-widest text-[11px] text-foreground-500">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>{activeRow.meta}
                  </p>
                )}
                {activeRow.regions.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Regions</span>
                    {activeRow.regions.map((id) => (
                      <span key={id} className="bg-primary-100 text-primary-700 border border-primary-200 font-label font-semibold uppercase tracking-wide text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">{regionName(id)}</span>
                    ))}
                  </div>
                )}
                <div className="mt-4 pt-3 border-t border-background-200 flex flex-wrap items-center gap-2">
                  <span className={`font-label font-semibold uppercase text-[10px] tracking-wide px-2.5 py-1 rounded-sm whitespace-nowrap ${statusStyle(activeRow.status)}`}>{statusLabel(activeRow.status)}</span>
                  <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">by {activeRow.submitter}</span>
                  {activeRow.status === 'pending' && (
                    <span className="font-label uppercase tracking-widest text-[10px] text-accent-800">Hidden from the live board until approved</span>
                  )}
                </div>
              </article>
            ) : (
              <p className="mt-4 text-sm text-foreground-500">Select a notice from the queue to preview how it would look on the public Community Board.</p>
            )}
          </div>
        </div>

        <div className="xl:col-span-7">
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3.5 border-b border-background-200">
              <div className="flex items-center gap-1 p-1 bg-background-100 border border-background-200 rounded-full w-full sm:w-auto sm:inline-flex overflow-x-auto">
                {FILTERS.map((f) => {
                  const count = f.id === 'all' ? counts.all : counts[f.id];
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setFilter(f.id)}
                      className={`flex items-center gap-2 px-4 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-[11px] whitespace-nowrap cursor-pointer transition-colors ${filter === f.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}
                    >
                      {f.label}
                      <span className={`text-[10px] px-1.5 rounded-full ${filter === f.id ? 'bg-background-50/20 text-background-50' : 'bg-background-200 text-foreground-600'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {loading && <p className="px-5 py-6 text-sm text-foreground-500">Loading bulletins&hellip;</p>}
            {error && (
              <div className="px-5 py-6">
                <p className="text-sm text-foreground-700">We couldn&apos;t load the bulletin queue. {error}</p>
                <button type="button" onClick={reload} className="mt-3 font-label uppercase tracking-wide text-[11px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer">Retry</button>
              </div>
            )}

            {!loading && !error && (
              <ul className="divide-y divide-background-200 max-h-[560px] overflow-y-auto">
                {rows.length === 0 && (
                  <li className="px-5 py-10 text-center text-sm text-foreground-500">
                    {filter === 'pending' ? 'No notices waiting for review — the queue is clear.' : 'Nothing here yet.'}
                  </li>
                )}
                {rows.map((r) => (
                  <li key={r.key} className={`px-5 py-4 transition-colors ${selected === r.key ? 'bg-background-100' : ''}`}>
                    <div className="flex items-start gap-4">
                      <button type="button" onClick={() => setSelected(r.key)} className="min-w-0 flex-1 text-left cursor-pointer group">
                        <span className="inline-block bg-primary-50 text-primary-700 font-label font-semibold uppercase text-[10px] tracking-[0.16em] px-2 py-0.5 rounded-sm">{r.tag}</span>
                        <p className="mt-1.5 font-medium text-foreground-950 truncate group-hover:text-primary-600 transition-colors">{r.title}</p>
                        <p className="text-xs text-foreground-500 truncate">
                          {r.submitter}{r.meta ? ` \u00b7 ${r.meta}` : ''}{r.regions.length ? ` \u00b7 ${r.regions.map(regionName).join(', ')}` : ''}{r.local ? ' \u00b7 local draft' : ''}
                        </p>
                      </button>
                      <span className={`font-label font-semibold uppercase text-[10px] tracking-wide px-2.5 py-1 rounded-sm whitespace-nowrap mt-0.5 ${statusStyle(r.status)}`}>{statusLabel(r.status)}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 pl-0">
                      {r.status === 'pending' ? (
                        <>
                          <button type="button" onClick={() => decide(r, 'approved')} className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-[11px] px-3 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line"></i></span>Approve
                          </button>
                          <button type="button" onClick={() => decide(r, 'rejected')} className="inline-flex items-center gap-1.5 border border-background-300 text-foreground-700 hover:border-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line"></i></span>Not approved
                          </button>
                        </>
                      ) : (
                        <button type="button" onClick={() => decide(r, 'pending')} className="inline-flex items-center gap-1.5 border border-background-300 text-foreground-700 hover:border-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-go-back-line"></i></span>Return to queue
                        </button>
                      )}
                      <button type="button" onClick={() => setSelected(r.key)} className="inline-flex items-center gap-1.5 font-label uppercase tracking-wide text-[11px] font-semibold text-foreground-600 hover:text-primary-600 px-2 py-2 cursor-pointer whitespace-nowrap">
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line"></i></span>Preview
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {flash && (
            <p className="mt-4 bg-secondary-50 border border-secondary-200 text-secondary-900 text-sm rounded-md px-4 py-3 flex items-start gap-2">
              <span className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-information-line"></i></span>{flash}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}